// import { Loader, palette, spacing } from "@stepapp/ui";
import React, { useEffect, useMemo } from "react";
import { StyleSheet } from "react-native";
import Animated, {
  interpolate,
  useAnimatedProps,
  useDerivedValue,
  useSharedValue,
  withRepeat,
  withTiming,
} from "react-native-reanimated";
import Svg, { Circle, Defs, LinearGradient, Stop } from "react-native-svg";

import { palette, spacing } from "../styles";

const AnimatedCircle = Animated.createAnimatedComponent(Circle);
const { PI } = Math;

type Props = {
  size?: number;
};

const CircleLoader = ({ size = spacing.unit * 8 }: Props) => {
  const { r, cx, cy, circumference, strokeWidth } = useMemo(() => {
    const strokeWidth = size / 2;
    const radius = (size - strokeWidth) / 2;
    return {
      strokeWidth,
      r: radius,
      cx: size / 2,
      cy: size / 2,
      circumference: radius * 2 * PI,
    };
  }, [size]);

  const progress = useSharedValue(-1);

  const strokeDashoffset = useDerivedValue(() => {
    const a = interpolate(progress.value, [-1, 0, 1], [-PI * 2, 0, PI * 2]);
    return a * r;
  });

  useEffect(() => {
    progress.value = withRepeat(withTiming(1, { duration: 2500 }), 0);
    return () => {
      progress.value = -1;
    };
  }, [progress]);

  const animatedCircleProps = useAnimatedProps(
    () => ({
      strokeDashoffset: strokeDashoffset.value,
    }),
    [strokeDashoffset],
  );

  return (
    <Svg height={size} style={styles.container} width={size}>
      <Defs>
        <LinearGradient id="grad" x1="0" x2="100%" y1="0" y2="0">
          <Stop offset="0" stopColor={palette.primary.main} />
          <Stop offset="0" stopColor={palette.primary.main} />
        </LinearGradient>
      </Defs>
      <AnimatedCircle
        animatedProps={animatedCircleProps}
        cx={cx}
        cy={cy}
        fill="none"
        r={r}
        stroke="url(#grad)"
        strokeDasharray={`${circumference}, ${circumference}`}
        strokeWidth={strokeWidth}
      />
    </Svg>
  );
};

export default React.memo(CircleLoader);

const styles = StyleSheet.create({
  container: {
    transform: [{ rotateZ: "-90deg" }],
  },
});
